import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/infrastructure/api/client'
import { selectUserRole, useAuthStore } from '@/application/stores/auth.store'
import type { SupportedLangCode } from '@/application/i18n/language'
import type {
  CreateTaxonomyTermResult,
  ManagedTaxonomyTerm,
  TaxonomyTerm,
  TaxonomyTermOption,
} from '@/domains/types'

const TAXONOMY_API_BASE = '/taxonomy'

export type TaxonomyVocabulary = 'category'

export const taxonomyKeys = {
  all: ['taxonomy'] as const,
  managed: (vocabulary: TaxonomyVocabulary) => ['taxonomy', vocabulary, 'managed'] as const,
  terms: (
    vocabulary: TaxonomyVocabulary,
    locale: SupportedLangCode,
    query: string,
    limit: number,
    viewerRole: string | undefined,
  ) => ['taxonomy', vocabulary, 'terms', locale, query, limit, viewerRole ?? 'anonymous'] as const,
}

interface UseGetTaxonomyTermsParams {
  vocabulary?: TaxonomyVocabulary
  locale: SupportedLangCode
  /** Free-text filter; an empty string returns the most used terms. */
  query: string
  limit: number
  enabled?: boolean
}

/**
 * Term suggestions for the picker: the most used terms without a query,
 * otherwise a case/accent-insensitive search. Labelled in `locale` (the
 * investigation's source locale). The previous result stays on screen while a
 * new query loads, so the list never flashes empty.
 * The result depends on the viewer (editors get every term, anyone else only
 * those of public investigations), hence the role in the cache key.
 */
export function useGetTaxonomyTerms({
  vocabulary = 'category',
  locale,
  query,
  limit,
  enabled = true,
}: UseGetTaxonomyTermsParams) {
  const viewerRole = useAuthStore(selectUserRole)
  return useQuery({
    queryKey: taxonomyKeys.terms(vocabulary, locale, query, limit, viewerRole),
    queryFn: async (): Promise<TaxonomyTermOption[]> => {
      const response = await apiClient.get<TaxonomyTermOption[]>(
        `${TAXONOMY_API_BASE}/${vocabulary}/terms`,
        // A failed suggestion lookup must not toast on every keystroke.
        { params: { q: query || undefined, locale, limit }, _skipErrorToast: true },
      )
      return response.data
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled,
  })
}

/**
 * Find-or-create a term by name (the backend matches case/accent-insensitively,
 * on names and labels). Deliberately has no `onError`: rethrowing there is
 * reported by TanStack as an extra unhandled rejection on top of the one
 * `mutateAsync` already surfaces, so the caller formats the failure itself with
 * `handleApiError`.
 */
export function useCreateTaxonomyTerm(vocabulary: TaxonomyVocabulary = 'category') {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      name: string
      locale: SupportedLangCode
    }): Promise<CreateTaxonomyTermResult> => {
      const response = await apiClient.post<CreateTaxonomyTermResult>(
        `${TAXONOMY_API_BASE}/${vocabulary}/terms`,
        data,
        // The picker reports the failure itself, once.
        { _skipErrorToast: true },
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxonomyKeys.all })
    },
  })
}

/** Labels typed in the management dialog; a blank one means "no label". */
export type ManagedTermLabels = Partial<Record<SupportedLangCode, string>>

/** Every term with its labels and usage, for the management list. */
export function useGetManagedTerms(vocabulary: TaxonomyVocabulary = 'category') {
  return useQuery({
    queryKey: taxonomyKeys.managed(vocabulary),
    queryFn: async (): Promise<ManagedTaxonomyTerm[]> => {
      const response = await apiClient.get<ManagedTaxonomyTerm[]>(
        `${TAXONOMY_API_BASE}/${vocabulary}/managed-terms`,
      )
      return response.data
    },
  })
}

/**
 * Create (no `id`) or update (with `id`) a term from its labels. No `onError`,
 * for the reason given on `useCreateTaxonomyTerm`: the dialog reports the
 * failure itself.
 */
export function useSaveManagedTerm(vocabulary: TaxonomyVocabulary = 'category') {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      termId,
      labels,
    }: {
      termId?: string
      labels: ManagedTermLabels
    }): Promise<TaxonomyTerm> => {
      const baseUrl = `${TAXONOMY_API_BASE}/${vocabulary}/managed-terms`
      const response = termId
        ? await apiClient.put<TaxonomyTerm>(
            `${baseUrl}/${termId}`,
            { labels },
            { _skipErrorToast: true },
          )
        : await apiClient.post<TaxonomyTerm>(baseUrl, { labels }, { _skipErrorToast: true })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxonomyKeys.all })
    },
  })
}

export interface DeleteManagedTermVariables {
  termId: string
  /** Replacement term receiving the investigations of a used term (409 without it). */
  reassignTo?: string
}

/** Deletes a term; a used one needs `reassignTo` (the backend answers 409 otherwise). */
export function useDeleteManagedTerm(vocabulary: TaxonomyVocabulary = 'category') {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ termId, reassignTo }: DeleteManagedTermVariables): Promise<void> => {
      await apiClient.delete(`${TAXONOMY_API_BASE}/${vocabulary}/managed-terms/${termId}`, {
        params: reassignTo ? { reassignTo } : undefined,
        _skipErrorToast: true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxonomyKeys.all })
    },
  })
}
